import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="slideover"
export default class extends Controller {
  static targets = ["dialog", "template", "openFullPageLink"]
  
  static values = {
    open: { type: Boolean, default: false },
    lazyLoad: { type: Boolean, default: false },
    turboFrameSrc: { type: String, default: "" },
    direction: { type: String, default: "right" }
  }

  connect() {
    // Handle Turbo caching - close slideover before page is cached
    document.addEventListener("turbo:before-cache", this.closeBeforeCache)
    
    // Listen for custom close event from turbo streams
    window.addEventListener("slideover:close-requested", this.handleCloseRequest)

    // Listen for a request to open this slideover from OUTSIDE its own
    // subtree — e.g. modal#closeAndOpenSlideover handing off from a pick
    // modal's "Create my own" link (TASK-226.8), where a plain
    // click->slideover#open action can't reach this controller.
    window.addEventListener("slideover:open-requested", this.handleOpenRequest)

    // Handle browser back button to close slideover
    window.addEventListener("popstate", this.handlePopState)
    
    // Listen for turbo-frame load to focus first input
    this.element.addEventListener("turbo:frame-load", this.handleFrameLoad)
    
    // Track if we pushed a history state
    this.pushedState = false
    
    // Open slideover on initial load if open value is true
    if (this.openValue) {
      this.open()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.closeBeforeCache)
    window.removeEventListener("slideover:close-requested", this.handleCloseRequest)
    window.removeEventListener("slideover:open-requested", this.handleOpenRequest)
    window.removeEventListener("popstate", this.handlePopState)
    this.element.removeEventListener("turbo:frame-load", this.handleFrameLoad)
    
    // Clean up body scroll lock
    document.body.style.overflow = ""
  }

  // Open slideover as a modal dialog
  open(event) {
    if (event) event.preventDefault()
    
    // Handle lazy loading
    if (this.lazyLoadValue && this.hasTemplateTarget) {
      this.loadTemplateContent()
    }
    
    // Handle Turbo Frame lazy loading, slideover direction, and slideover title - check both slideover container and triggering button
    let turboFrameSrc = this.turboFrameSrcValue
    let slideoverDirection = this.directionValue
    let slideoverTitle = null
    
    let slideoverSize = null

    if (event && event.target) {
      // Find the element with the data attributes, traversing up the DOM tree if needed
      let element = event.target
      let buttonTurboFrameSrc = null
      let buttonSlideoverDirection = null
      let buttonSlideoverTitle = null
      let buttonSlideoverSize = null

      while (element && element !== document.body) {
        if (!buttonTurboFrameSrc) {
          buttonTurboFrameSrc = element.getAttribute('data-slideover-turbo-frame-src-value')
        }
        if (!buttonSlideoverDirection) {
          buttonSlideoverDirection = element.getAttribute('data-slideover-direction-value')
        }
        if (!buttonSlideoverTitle) {
          buttonSlideoverTitle = element.getAttribute('data-slideover-title-value')
        }
        if (!buttonSlideoverSize) {
          buttonSlideoverSize = element.getAttribute('data-slideover-size-value')
        }
        // Continue searching until we've checked all possible elements
        element = element.parentElement
      }

      if (buttonTurboFrameSrc) {
        turboFrameSrc = buttonTurboFrameSrc
      }
      if (buttonSlideoverDirection) {
        slideoverDirection = buttonSlideoverDirection
      }
      if (buttonSlideoverTitle) {
        slideoverTitle = buttonSlideoverTitle
      }
      if (buttonSlideoverSize) {
        slideoverSize = buttonSlideoverSize
      }
    }
    
    if (turboFrameSrc) {
      this.loadTurboFrame(turboFrameSrc)
      // Set the full page link URL
      this.setFullPageLink(turboFrameSrc)
    }
    
    // Apply slideover direction if specified
    if (slideoverDirection) {
      this.applySlideoverDirection(slideoverDirection)
    }
    
    // Apply slideover title if specified
    if (slideoverTitle) {
      this.applySlideoverTitle(slideoverTitle)
    }

    // Apply slideover size if specified
    this.applySlideoverSize(slideoverSize)

    // Show the slideover
    this.dialogTarget.showModal()
    
    // Lock body scroll
    document.body.style.overflow = "hidden"
    
    // Remove closing attribute if present
    this.dialogTarget.removeAttribute("data-closing")
    
    // Update URL in browser history if we have a turbo frame source
    if (turboFrameSrc && turboFrameSrc !== "" && turboFrameSrc !== "#") {
      // Store the current URL and its history state before pushing new state
      this.originalUrl = window.location.href
      this.originalState = history.state
      // Push the new URL to history
      history.pushState({ slideover: true }, "", turboFrameSrc)
      this.pushedState = true
    }
    
    // Dispatch custom event
    this.dispatch("opened")
  }

  // Stop click propagation so nested links/buttons don't trigger the slideover open
  stop(event) {
    if (event) event.stopPropagation()
  }

  // Close slideover with animation
  close(event) {
    if (event) event.preventDefault()
    
    // Add closing state for animation
    this.dialogTarget.setAttribute("data-closing", "")
    
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      this.dialogTarget.close()
      this.dialogTarget.removeAttribute("data-closing")
      
      // Unlock body scroll
      document.body.style.overflow = ""
      
      // Clear the turbo-frame content to prevent stale data
      const turboFrame = this.dialogTarget.querySelector("turbo-frame[data-slideover-frame]")
      if (turboFrame) {
        turboFrame.removeAttribute("src")
        turboFrame.innerHTML = '<div class="turbo-loading"></div>'
      }
      
      // Hide the full page link
      if (this.hasOpenFullPageLinkTarget) {
        this.openFullPageLinkTarget.href = "#"
        this.openFullPageLinkTarget.style.display = "none"
      }
      
      // Restore original URL if we pushed a state
      if (this.pushedState && this.originalUrl) {
        // Use replaceState to avoid adding another history entry. Write back
        // the state captured on open, not null: Turbo keeps its
        // restorationIdentifier in history.state, and its popstate handler
        // ignores an entry without one — so Back to this page would change
        // the URL and leave the next page on screen (TASK-201).
        history.replaceState(this.originalState, "", this.originalUrl)
        this.pushedState = false
        this.originalUrl = null
        this.originalState = null
      }
      
      // Dispatch custom event
      this.dispatch("closed")
    }, 200) // Match animation duration in CSS
  }

  // Handle backdrop click (clicking outside slideover content)
  backdropClose(event) {
    // Only close if clicking directly on the dialog (backdrop), not its children
    if (event.target === this.dialogTarget) {
      this.close(event)
    }
  }

  // Handle escape key
  cancel(event) {
    // Prevent the default browser behavior
    event.preventDefault()
    event.stopPropagation()
    
    // Close the slideover
    this.close()
  }

  // Alias for close
  hide(event) {
    this.close(event)
  }

  // Show slideover non-modally (for special use cases)
  show(event) {
    if (event) event.preventDefault()
    
    this.dialogTarget.show()
    document.body.style.overflow = "hidden"
  }

  // Load template content (Template-based Lazy Loading)
  loadTemplateContent() {
    if (!this.hasTemplateTarget) return
    
    const template = this.templateTarget
    const content = template.content.cloneNode(true)
    
    // Find the slideover body or insert point
    const slideoverBody = this.dialogTarget.querySelector(".slideover-body")
    if (slideoverBody) {
      // Clear existing loading state if any
      slideoverBody.innerHTML = ""
      slideoverBody.appendChild(content)
    }
    
    // Mark as loaded so we don't load again
    this.lazyLoadValue = false
  }

  // Load Turbo Frame content (Turbo Frame Lazy Loading)
  loadTurboFrame(src) {
    const turboFrame = this.dialogTarget.querySelector("turbo-frame[data-slideover-frame]")
    if (turboFrame && !turboFrame.hasAttribute("src")) {
      turboFrame.setAttribute("src", src)
    }
  }

  // Apply slideover direction class to dialog
  applySlideoverDirection(direction) {
    const dialog = this.dialogTarget
    if (dialog) {
      // Remove existing direction classes
      dialog.classList.remove("slideover-right", "slideover-left", "slideover-top", "slideover-bottom")
      // Add the new direction class
      dialog.classList.add(`slideover-${direction}`)
    }
  }

  // Apply slideover title to slideover header
  applySlideoverTitle(title) {
    const slideoverTitleElement = this.dialogTarget.querySelector(".slideover-header h3, .slideover-header h2, .slideover-header h1")
    if (slideoverTitleElement) {
      slideoverTitleElement.textContent = title
    }
  }

  // Apply slideover size class to dialog
  applySlideoverSize(size) {
    const dialog = this.dialogTarget
    if (dialog) {
      dialog.classList.remove("slideover-sm", "slideover-md", "slideover-lg", "slideover-xl")
      if (size) {
        dialog.classList.add(`slideover-${size}`)
      }
    }
  }

  // Set the full page link URL
  setFullPageLink(url) {
    if (this.hasOpenFullPageLinkTarget && url && url !== "" && url !== "#") {
      this.openFullPageLinkTarget.href = url
      this.openFullPageLinkTarget.style.display = ""
    }
  }

  // Close before Turbo caches the page
  closeBeforeCache = () => {
    if (this.dialogTarget.open) {
      this.dialogTarget.close()
      document.body.style.overflow = ""
    }
  }

  // Handle open request dispatched from OUTSIDE this controller's own
  // element (TASK-226.8) — `open(event)` normally reads its src/title/etc
  // off `event.target` by walking up the DOM, so hand it a detached element
  // carrying the same data attributes rather than duplicating that logic.
  handleOpenRequest = (event) => {
    const { turboFrameSrc, title, direction, size } = event.detail || {}
    const el = document.createElement("div")
    if (turboFrameSrc) el.setAttribute("data-slideover-turbo-frame-src-value", turboFrameSrc)
    if (title) el.setAttribute("data-slideover-title-value", title)
    if (direction) el.setAttribute("data-slideover-direction-value", direction)
    if (size) el.setAttribute("data-slideover-size-value", size)

    this.open({ preventDefault: () => {}, target: el })
  }

  // Handle close request from turbo stream
  handleCloseRequest = () => {
    if (this.dialogTarget.open) {
      this.close()
    }
  }

  // Handle browser back button
  handlePopState = (event) => {
    // If slideover is open and user pressed back, close it
    if (this.dialogTarget.open) {
      // Don't let the default back navigation happen
      event.preventDefault()
      // Mark that we're handling the popstate, not pushing
      this.pushedState = false
      // Close the slideover
      this.close()
    }
  }

  // Handle turbo frame load to focus first input
  handleFrameLoad = () => {
    if (this.dialogTarget.open) {
      setTimeout(() => {
        const firstInput = this.dialogTarget.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])')
        if (firstInput) {
          firstInput.focus()
        }
      }, 50)
    }
  }
}

